import moment from 'moment';

/**
 * V12 Finance Calculator
 * Handles all finance calculations for V12 products
 */
export class V12Calculator {
  constructor(settings = {
    retailerId: 0,
    retailerGuid: "00000000-0000-0000-0000-000000000000"
  }) {
    this.settings = settings;
  }

  /**
   * Calculate APR from IRR
   */
  calculateAprFromIrr(loan, monthlyInstalment, loanTerm, documentFee, documentFeeCollectionMonth) {
    const startDate = new Date();
    const incomeTable = [];
    const dateTable = [];

    if (documentFeeCollectionMonth === 0) {
      incomeTable.push(parseFloat(loan * -1) + documentFee);
    } else {
      incomeTable.push(parseFloat(loan * -1));
    }
    dateTable.push(startDate);

    for (let i = 1; i <= loanTerm; i++) {
      const nextDate = moment(startDate).add('M', i);
      dateTable.push(nextDate);

      if ((i - 1) === documentFeeCollectionMonth && documentFeeCollectionMonth > 0) {
        incomeTable.push(parseFloat(monthlyInstalment + documentFee));
      } else {
        incomeTable.push(parseFloat(monthlyInstalment));
      }
    }
    const r = this.XIRR(incomeTable, dateTable, 0.1);
    return Math.round(r * 10000) / 100;
  }

  /**
   * Calculate finance details for a product
   */
  calculate(financeProduct, cashPrice, deposit) {
    var apr = parseFloat(financeProduct.apr);
    var monthlyrate = parseFloat(financeProduct.monthlyRate);
    var calculatedApr;
    var months = parseFloat(financeProduct.months);
    var serviceFee = parseFloat(financeProduct.serviceFee);
    var balancePayable = parseFloat(0.00);
    var documentFee = 0;

    cashPrice = parseFloat(cashPrice);

    deposit = parseFloat(deposit);

    var loanAmount = cashPrice - deposit;
    var initialPayments, finalPayment, balancePayable;

    balancePayable = loanAmount;

    documentFee = financeProduct.documentFee + (loanAmount * financeProduct.documentFeePercentage);

    if (financeProduct.documentFeeMinimum>0 && documentFee < financeProduct.documentFeeMinimum) {documentFee = financeProduct.documentFeeMinimum}
    if (financeProduct.documentFeeMaximum>0 && documentFee > financeProduct.documentFeeMaximum) {documentFee = financeProduct.documentFeeMaximum}

    if (monthlyrate == 0) {
      initialPayments = Math.round((loanAmount / months) * 100) / 100;

      if (initialPayments * months < loanAmount) {
        initialPayments += 0.01;
      }

      finalPayment = (loanAmount - (initialPayments * (months - 1)));

      calculatedApr = 0;
    } else {
      var yield1 = Math.pow(((apr) / 100) + 1, (1.00 / 12));

      var pv = loanAmount - serviceFee;

      if (financeProduct.deferredPeriod>1) {
        pv = (pv * Math.pow(yield1, (financeProduct.deferredPeriod - 1)));
      }

      initialPayments = Math.ceil(((0 - pv / ((Math.pow(yield1, 0 - months) - 1) / (yield1 - 1)))) * 100) / 100;

      finalPayment = initialPayments;

      balancePayable = (initialPayments * months);

      calculatedApr = this.calculateApr((loanAmount - financeProduct.serviceFee), initialPayments, financeProduct.deferredPeriod, months);
    }

    if (documentFee>0) {
      calculatedApr = this.calculateAprFromIrr(loanAmount, initialPayments, months, parseFloat(documentFee), parseFloat(financeProduct.documentFeeCollectionMonth));
    }

    //balancePayable = initialPayments * (months - 1);
    //balancePayable += finalPayment;

    var interest = (balancePayable - loanAmount);

    var chargeForCredit = (interest + serviceFee + documentFee);

    var amountPayable = (balancePayable + serviceFee + documentFee + deposit);
    
    var productAvailable = true;
    var availabilityReason = "";

    if (loanAmount < financeProduct.minLoan) {
      productAvailable = false;

      availabilityReason = "Only available on loan amounts over £" + financeProduct.minLoan.toFixed(2);
    } else if (loanAmount > financeProduct.maxLoan) {
      productAvailable = false;

      availabilityReason = "Only available on loan amounts under £" + financeProduct.maxLoan.toFixed(2);
    }

    var annualRate = (((interest / loanAmount) * 100) / ((months + financeProduct.deferredPeriod)/ 12));

    var financeCalculation = {
      initialPayments: initialPayments.toFixed(2),
      finalPayment: finalPayment.toFixed(2),
      balancePayable: balancePayable.toFixed(2),
      interest: interest.toFixed(2),
      chargeForCredit: chargeForCredit.toFixed(2),
      amountPayable: amountPayable.toFixed(2),
      cashPrice: cashPrice.toFixed(2),
      deposit: deposit.toFixed(2),
      loanAmount: loanAmount.toFixed(2),
      months: months,
      monthsDeferred: financeProduct.deferredPeriod,
      apr: calculatedApr.toFixed(2),
      productAvailable: productAvailable,
      availabilityReason: availabilityReason,
      productId: financeProduct.productId,
      productGuid: financeProduct.productGuid,
      name: financeProduct.name,
      settlementFee: financeProduct.settlementFee.toFixed(2),
      serviceFee: serviceFee.toFixed(2),
      documentFee: documentFee.toFixed(2),
      documentFeeMinimum: financeProduct.documentFeeMinimum,
      documentFeeMaximum: financeProduct.documentFeeMaximum,
      documentFeeCollectionMonth: financeProduct.documentFeeCollectionMonth,
      documentFeePercentage: financeProduct.documentFeePercentage,
      annualRate: annualRate.toFixed(2)
    };

    return financeCalculation;
  };

  /**
   * Calculate APR
   */
  calculateApr(loan, instalment, deferred, term) {
    let result = parseFloat(0);
    let high = parseFloat(200);
    let low = parseFloat(0);

    const n = deferred > 1 ? (term + deferred + 1) : (term + 1);
    let x = 1;

    while (x < 20) {
      result = (high + low) / 2;
      const j = parseFloat(Math.pow((1.0000 + result / 100.0000), (1.0000 / 12.0000)));
      const q = parseFloat((1.0000 / j));

      let y, z;
      if (deferred < 1) {
        y = parseFloat((instalment * (1.0000 - Math.pow(q, n))) / (1 - q) - instalment);
        z = parseFloat(0.00);
      } else {
        y = parseFloat((instalment * (1.0000 - Math.pow(q, n - 1))) / (1 - q) - instalment);
        z = parseFloat((instalment * (1.0000 - Math.pow(q, deferred))) / (1 - q) - instalment);
      }

      if ((y - z) < loan) {
        high = result;
      } else {
        low = result;
      }

      x++;
    }

    return result;
  }

  /**
   * Calculate XIRR
   */
  XIRR(values, dates, guess = 0.1) {
    const irrResult = (values, dates, rate) => {
      const r = rate + 1;
      let result = values[0];
      for (let i = 1; i < values.length; i++) {
        result += values[i] / Math.pow(r, moment(dates[i]).diff(moment(dates[0]), 'days') / 365);
      }
      return result;
    };

    const irrResultDeriv = (values, dates, rate) => {
      const r = rate + 1;
      let result = 0;
      for (let i = 1; i < values.length; i++) {
        const frac = moment(dates[i]).diff(moment(dates[0]), 'days') / 365;
        result -= frac * values[i] / Math.pow(r, frac + 1);
      }
      return result;
    };

    // Check that values contains at least one positive value and one negative value
    const hasPositive = values.some(v => v > 0);
    const hasNegative = values.some(v => v < 0);
    if (!hasPositive || !hasNegative) return '#NUM!';

    let resultRate = guess;
    const epsMax = 1e-10;
    const iterMax = 50;

    let iteration = 0;
    let contLoop = true;
    
    do {
      const resultValue = irrResult(values, dates, resultRate);
      const newRate = resultRate - resultValue / irrResultDeriv(values, dates, resultRate);
      const epsRate = Math.abs(newRate - resultRate);
      resultRate = newRate;
      contLoop = (epsRate > epsMax) && (Math.abs(resultValue) > epsMax);
    } while (contLoop && (++iteration < iterMax));

    if (contLoop) return '#NUM!';
    return resultRate;
  }
}

export default V12Calculator; 